/**
 * The always-on spatial layer: each dashboard gets a "stage" on the floor in front of its
 * frame, where its data stands as objects (ramps, towers, carpets, scatter cubes, people).
 * All eleven stages render through six instanced meshes (boxes, spheres, cylinders, glass,
 * two figure bodies), one line buffer for axes and one shared canvas atlas for the label
 * strips: eight draw calls in all. Every object has a tooltip and most can be selected.
 */
import * as THREE from 'three';
import { mergeGeometries } from 'three/examples/jsm/utils/BufferGeometryUtils.js';
import type { DashboardFilters, DashboardId } from '@clinspace/shared';
import { PALETTE, fillRoundRect, font, text, truncate } from './charts.ts';
import type { InteractiveSystem } from '../ui/Interactive.ts';

/**
 * Stage-local metres: x across the frame, z toward the table, y up. Objects stay under
 * yMax so the label strip at the back of the stage (under the panel) is readable over them.
 */
export const STAGE = { xMin: -1.15, xMax: 1.15, zMin: 0.3, zMax: 1.4, yMax: 0.75 };

export type StageAction =
  { kind: 'drill'; path: string[] } | { kind: 'filter'; patch: Partial<DashboardFilters> };

interface Base {
  colour: string;
  tooltip: string[];
  select?: StageAction;
}
export interface StageBox extends Base {
  kind: 'box';
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
  rotY?: number;
}
export interface StageSphere extends Base {
  kind: 'sphere';
  x: number;
  y: number;
  z: number;
  r: number;
}
export interface StageCylinder extends Base {
  kind: 'cylinder';
  x: number;
  y: number;
  z: number;
  r: number;
  h: number;
}
/** A small standing person, male or female body, `h` tall, facing +z. */
export interface StageFigure extends Base {
  kind: 'figure';
  sex: 'M' | 'F';
  x: number;
  y: number;
  z: number;
  h: number;
}
export interface StageGlass {
  kind: 'glass';
  x: number;
  y: number;
  z: number;
  w: number;
  h: number;
  d: number;
  colour: string;
}
export type StageItem = StageBox | StageSphere | StageCylinder | StageFigure;

export interface StageLabel {
  title: string;
  legend: { label: string; colour: string }[];
  caption?: string;
  /** Labels along the strip at stage x positions. */
  xLabels?: { x: number; text: string }[];
}

/**
 * Words standing in the stage (2026-10-05, the user's request): callouts that point out the
 * metric that matters most without anyone pointing at it (a Hy's law case, a fatal SAE, the
 * oldest queries), and axis names. A callout stands on a leader line above (x, y, z), the top
 * of its object; an axis name is centred on (x, y, z).
 */
export interface StageNote {
  kind: 'callout' | 'axis';
  x: number;
  y: number;
  z: number;
  title: string;
  detail?: string;
  colour: string;
}

export interface StageScene {
  items: StageItem[];
  glass?: StageGlass[];
  /** Axis lines as pairs of stage-local points. */
  lines?: [number, number, number, number, number, number][];
  /** Callouts and axis names; at most NOTES_PER_STAGE are drawn. */
  notes?: StageNote[];
  label: StageLabel;
}

/** Notes per stage, their tiles in the notes atlas, and their size in stage metres. */
export const NOTES_PER_STAGE = 5;
const NOTE_TILE = { w: 384, h: 96 };
const NOTE_COLS = 4;
export const NOTE_SIZE = {
  callout: { w: 0.62, h: 0.155, lift: 0.14 },
  axis: { w: 0.46, h: 0.115 },
};

export interface StageFrame {
  dashboard: DashboardId;
  /** Floor point under the panel frame and the frame's yaw (local +z faces the table). */
  origin: { x: number; z: number };
  yaw: number;
  /** A copy of a stage at another size (the centre screen's, the table's miniature). */
  scale?: number;
  /** Height of the stage's floor (a miniature stands on the table). */
  y?: number;
  /** False leaves out the label strip (the screen behind or the console says what it is). */
  labels?: boolean;
}

type MeshKey = 'box' | 'sphere' | 'cylinder' | 'figureM' | 'figureF';

const CAP: Record<MeshKey | 'glass' | 'lines', number> = {
  box: 4000,
  sphere: 2000,
  cylinder: 400,
  figureM: 600,
  figureF: 600,
  glass: 60,
  lines: 600,
};
const TILE_W = 1024;
const TILE_H = 134;
/** Atlas pixels per logical tile pixel: sharper strips a few metres away. */
const ATLAS_SCALE = 1.5;
const LABEL_W = 2.3;
const LABEL_H = 0.3;
/** The strip hangs at the back of the stage, under the panel, above the objects. */
const LABEL_Y = 0.92;
const LABEL_Z = 0.12;

interface Entry {
  dashboard: DashboardId;
  item: StageItem;
}

/**
 * A one-metre-tall low-poly person, scaled per item. Pictogram silhouettes so the two bodies
 * read apart at 20 cm: male has broad shoulders, straight legs and a flat cap; female has
 * narrow shoulders over an A-line dress, a bob with a hairband and a high bun. Hair is baked
 * darker and the hairband lighter through vertex colours, so the instance colour (status)
 * still tints the whole body.
 */
export function buildFigureGeometry(sex: 'M' | 'F'): THREE.BufferGeometry {
  const parts: THREE.BufferGeometry[] = [];
  const add = (
    g: THREE.BufferGeometry,
    x: number,
    y: number,
    z: number,
    tint = 1,
    sx = 1,
    sy = 1,
    sz = 1,
    rotX = 0,
  ) => {
    g.scale(sx, sy, sz);
    if (rotX) g.rotateX(rotX);
    g.translate(x, y, z);
    const n = g.getAttribute('position').count;
    const colour = new Float32Array(n * 3).fill(tint);
    g.setAttribute('color', new THREE.BufferAttribute(colour, 3));
    parts.push(g);
  };
  const HAIR = 0.45;
  const BAND = 1.5;
  if (sex === 'M') {
    add(new THREE.SphereGeometry(0.075, 8, 6), 0, 0.9, 0, 1, 1, 0.95, 1);
    add(
      new THREE.SphereGeometry(0.078, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.45),
      0,
      0.905,
      0,
      HAIR,
    );
    // Flat cap with a short peak.
    add(new THREE.CylinderGeometry(0.085, 0.085, 0.03, 8), 0, 0.955, 0, HAIR);
    add(new THREE.BoxGeometry(0.16, 0.012, 0.06), 0, 0.945, 0.08, HAIR);
    add(new THREE.CylinderGeometry(0.03, 0.035, 0.05, 8), 0, 0.815, 0);
    add(new THREE.CylinderGeometry(0.19, 0.12, 0.38, 8), 0, 0.6, 0, 1, 1, 1, 0.55);
    add(new THREE.BoxGeometry(0.22, 0.08, 0.13), 0, 0.39, 0);
    for (const s of [-1, 1]) add(new THREE.CapsuleGeometry(0.032, 0.32, 2, 6), s * 0.22, 0.6, 0);
    for (const s of [-1, 1])
      add(new THREE.CylinderGeometry(0.05, 0.045, 0.36, 8), s * 0.06, 0.18, 0);
    for (const s of [-1, 1]) add(new THREE.BoxGeometry(0.09, 0.03, 0.14), s * 0.06, 0.015, 0.03);
  } else {
    add(new THREE.SphereGeometry(0.072, 8, 6), 0, 0.9, 0);
    // Bob: a cap that drops to the jaw at the sides and back.
    add(
      new THREE.SphereGeometry(0.082, 8, 6, 0, Math.PI * 2, 0, Math.PI * 0.62),
      0,
      0.9,
      -0.01,
      HAIR,
      1,
      1.15,
      1,
    );
    add(new THREE.TorusGeometry(0.078, 0.012, 5, 12), 0, 0.93, 0, BAND, 1, 1, 1, Math.PI / 2 - 0.5);
    // High bun.
    add(new THREE.SphereGeometry(0.045, 8, 6), 0, 0.995, -0.03, HAIR);
    add(new THREE.CylinderGeometry(0.028, 0.032, 0.05, 8), 0, 0.815, 0);
    add(new THREE.CylinderGeometry(0.14, 0.1, 0.26, 8), 0, 0.66, 0, 1, 1, 1, 0.55);
    // A-line dress: waist to knee, wide at the hem.
    add(new THREE.CylinderGeometry(0.1, 0.25, 0.3, 8), 0, 0.38, 0, 1, 1, 1, 0.75);
    for (const s of [-1, 1]) add(new THREE.CapsuleGeometry(0.03, 0.3, 2, 6), s * 0.17, 0.62, 0);
    for (const s of [-1, 1])
      add(new THREE.CylinderGeometry(0.04, 0.038, 0.24, 8), s * 0.055, 0.12, 0);
    for (const s of [-1, 1]) add(new THREE.BoxGeometry(0.075, 0.03, 0.12), s * 0.055, 0.015, 0.025);
  }
  const merged = mergeGeometries(parts, false);
  for (const p of parts) p.dispose();
  return merged;
}

/**
 * Places axis labels along a strip without overlaps or clipping: each label is centred on its
 * x where it can be, pushed inside the strip at the ends, and dropped when it would touch the
 * label before it (the first and last labels win). Returns left x positions.
 */
export function placeAxisLabels(
  labels: { text: string; x: number; w: number }[],
  min: number,
  max: number,
  gap: number,
): { text: string; x: number; maxWidth: number }[] {
  const sorted = labels.slice().sort((a, b) => a.x - b.x);
  const spot = (l: { x: number; w: number }) => {
    const w = Math.min(l.w, max - min);
    const left = Math.min(Math.max(min, l.x - w / 2), max - w);
    return { left, right: left + w, w };
  };
  const last = sorted.length ? spot(sorted[sorted.length - 1]!) : undefined;
  const out: { text: string; x: number; maxWidth: number }[] = [];
  let prevRight = -Infinity;
  sorted.forEach((l, i) => {
    const s = spot(l);
    const isLast = i === sorted.length - 1;
    if (s.left < prevRight + gap) return;
    if (!isLast && last && s.right + gap > last.left) return;
    out.push({ text: l.text, x: s.left, maxWidth: s.w });
    prevRight = s.right;
  });
  return out;
}

export class StageLayer {
  readonly group = new THREE.Group();
  private readonly meshes: Record<MeshKey, THREE.InstancedMesh>;
  private readonly glass: THREE.InstancedMesh;
  private readonly lines: THREE.LineSegments;
  private readonly atlas = document.createElement('canvas');
  private readonly atlasTexture: THREE.CanvasTexture;
  private readonly labelMesh: THREE.Mesh;
  private readonly scenes = new Map<DashboardId, StageScene>();
  private readonly frames = new Map<DashboardId, StageFrame>();
  private entries: Record<MeshKey, Entry[]> = {
    box: [],
    sphere: [],
    cylinder: [],
    figureM: [],
    figureF: [],
  };
  private hovered: { mesh: MeshKey; index: number } | undefined;
  /** The notes: one atlas and one mesh of quads for every stage's callouts and axis names. */
  private readonly notesAtlas = document.createElement('canvas');
  private readonly notesTexture: THREE.CanvasTexture;
  private readonly notesMesh: THREE.Mesh;
  private readonly noteRows: number;
  /** Label atlas tiles: two columns, as many rows as the frames need. */
  private readonly atlasCols: number;
  private readonly atlasRows: number;
  private atlasDirty = true;
  private instancesDirty = true;

  constructor(
    frames: StageFrame[],
    interactive: InteractiveSystem,
    private readonly handlers: {
      tooltip(lines: string[] | undefined, at: THREE.Vector3 | undefined): void;
      select(dashboard: DashboardId, item: StageItem, at: THREE.Vector3): void;
    },
  ) {
    for (const f of frames) this.frames.set(f.dashboard, f);
    this.atlasCols = Math.min(2, Math.max(1, frames.length));
    this.atlasRows = Math.max(1, Math.ceil(frames.length / this.atlasCols));
    // Bright, unfogged by tone mapping: the stage objects must read from across the room.
    const solid = () =>
      new THREE.MeshStandardMaterial({
        roughness: 0.4,
        metalness: 0,
        emissive: 0xffffff,
        emissiveIntensity: 0.06,
        toneMapped: false,
      });
    const make = (geometry: THREE.BufferGeometry, cap: number, name: string, tinted = false) => {
      const material = solid();
      material.vertexColors = tinted;
      const m = new THREE.InstancedMesh(geometry, material, cap);
      m.count = 0;
      m.frustumCulled = false;
      m.name = name;
      return m;
    };
    this.meshes = {
      box: make(new THREE.BoxGeometry(1, 1, 1), CAP.box, 'stage-boxes'),
      sphere: make(new THREE.SphereGeometry(1, 12, 10), CAP.sphere, 'stage-spheres'),
      cylinder: make(new THREE.CylinderGeometry(1, 1, 1, 20), CAP.cylinder, 'stage-cylinders'),
      figureM: make(buildFigureGeometry('M'), CAP.figureM, 'stage-figures-m', true),
      figureF: make(buildFigureGeometry('F'), CAP.figureF, 'stage-figures-f', true),
    };
    this.glass = new THREE.InstancedMesh(
      new THREE.BoxGeometry(1, 1, 1),
      new THREE.MeshStandardMaterial({
        roughness: 0.2,
        transparent: true,
        opacity: 0.3,
        depthWrite: false,
        emissive: 0xffffff,
        emissiveIntensity: 0.15,
        toneMapped: false,
      }),
      CAP.glass,
    );
    this.glass.count = 0;
    this.glass.frustumCulled = false;
    this.glass.name = 'stage-glass';
    const lineGeometry = new THREE.BufferGeometry();
    lineGeometry.setAttribute(
      'position',
      new THREE.BufferAttribute(new Float32Array(CAP.lines * 6), 3),
    );
    lineGeometry.setDrawRange(0, 0);
    this.lines = new THREE.LineSegments(
      lineGeometry,
      new THREE.LineBasicMaterial({ color: 0xa9b4c2, transparent: true, opacity: 0.7 }),
    );
    this.lines.frustumCulled = false;
    this.lines.name = 'stage-lines';

    this.atlas.width = TILE_W * this.atlasCols * ATLAS_SCALE;
    this.atlas.height = TILE_H * this.atlasRows * ATLAS_SCALE;
    this.atlasTexture = new THREE.CanvasTexture(this.atlas);
    this.atlasTexture.colorSpace = THREE.SRGBColorSpace;
    this.atlasTexture.anisotropy = 8;
    this.labelMesh = new THREE.Mesh(
      this.buildLabelGeometry(frames),
      new THREE.MeshBasicMaterial({ map: this.atlasTexture, transparent: true, toneMapped: false }),
    );
    this.labelMesh.name = 'stage-labels';
    this.noteRows = Math.max(1, Math.ceil((frames.length * NOTES_PER_STAGE) / NOTE_COLS));
    this.notesAtlas.width = NOTE_TILE.w * NOTE_COLS;
    this.notesAtlas.height = NOTE_TILE.h * this.noteRows;
    this.notesTexture = new THREE.CanvasTexture(this.notesAtlas);
    this.notesTexture.colorSpace = THREE.SRGBColorSpace;
    this.notesTexture.generateMipmaps = false;
    this.notesTexture.minFilter = THREE.LinearFilter;
    this.notesMesh = new THREE.Mesh(
      new THREE.BufferGeometry(),
      new THREE.MeshBasicMaterial({
        map: this.notesTexture,
        transparent: true,
        depthWrite: false,
        toneMapped: false,
      }),
    );
    this.notesMesh.name = 'stage-notes';
    this.notesMesh.frustumCulled = false;
    this.notesMesh.renderOrder = 6;
    this.group.add(this.notesMesh);
    this.labelMesh.frustumCulled = false;
    this.group.add(...Object.values(this.meshes), this.glass, this.lines, this.labelMesh);

    for (const key of Object.keys(this.meshes) as MeshKey[]) {
      const mesh = this.meshes[key];
      interactive.add({
        object: mesh,
        onHover: (_p, point, hit) => {
          const i = hit?.instanceId ?? -1;
          if (this.hovered?.mesh === key && this.hovered.index === i) return;
          this.hovered = { mesh: key, index: i };
          handlers.tooltip(this.entries[key][i]?.item.tooltip, point);
        },
        onLeave: () => {
          this.hovered = undefined;
          handlers.tooltip(undefined, undefined);
        },
        onSelect: (_p, point, hit) => {
          const e = this.entries[key][hit?.instanceId ?? -1];
          if (e) handlers.select(e.dashboard, e.item, point);
        },
      });
    }
  }

  /** The meshes rays can hit (for tests). */
  get hitObjects(): THREE.Object3D[] {
    return Object.values(this.meshes);
  }

  /** One quad per frame at the back of the stage under the panel, UV-mapped to its atlas tile. */
  private buildLabelGeometry(frames: StageFrame[]): THREE.BufferGeometry {
    const parts: THREE.BufferGeometry[] = [];
    frames.forEach((f, i) => {
      if (f.labels === false) return;
      const g = new THREE.PlaneGeometry(LABEL_W, LABEL_H);
      const uv = g.getAttribute('uv') as THREE.BufferAttribute;
      const cols = this.atlasCols;
      const rows = this.atlasRows;
      const col = i % cols;
      const row = Math.floor(i / cols);
      for (let k = 0; k < uv.count; k++) {
        uv.setXY(k, (col + uv.getX(k)) / cols, 1 - (row + 1 - uv.getY(k)) / rows);
      }
      const local = new THREE.Matrix4().makeRotationX(-0.18).setPosition(0, LABEL_Y, LABEL_Z);
      g.applyMatrix4(this.frameMatrix(f).multiply(local));
      parts.push(g);
    });
    return parts.length ? mergeGeometries(parts, false) : new THREE.BufferGeometry();
  }

  private frameMatrix(f: StageFrame): THREE.Matrix4 {
    const s = f.scale ?? 1;
    return new THREE.Matrix4()
      .makeRotationY(f.yaw)
      .scale(new THREE.Vector3(s, s, s))
      .setPosition(f.origin.x, f.y ?? 0, f.origin.z);
  }

  /**
   * Points a one-frame layer (a copy of a stage) at another dashboard, or at none. The frame's
   * place stays; its scene is cleared until the next `set`.
   */
  retarget(dashboard: DashboardId | undefined): void {
    const frame = [...this.frames.values()][0] ?? this.parked;
    if (!frame || (this.frames.size && frame.dashboard === dashboard)) return;
    this.frames.clear();
    this.scenes.clear();
    this.parked = frame;
    if (dashboard) this.frames.set(dashboard, { ...frame, dashboard });
    this.instancesDirty = true;
    this.atlasDirty = true;
  }

  /** The frame of a retargeted layer while it shows nothing. */
  private parked: StageFrame | undefined;

  /** The dashboard a one-frame layer shows. */
  get target(): DashboardId | undefined {
    return [...this.frames.keys()][0];
  }

  /** Replaces one dashboard's stage. Rebuilds the instance buffers on the next update. */
  set(dashboard: DashboardId, scene: StageScene | undefined): void {
    if (scene) this.scenes.set(dashboard, scene);
    else this.scenes.delete(dashboard);
    this.instancesDirty = true;
    this.atlasDirty = true;
  }

  scene(dashboard: DashboardId): StageScene | undefined {
    return this.scenes.get(dashboard);
  }

  /** Items of a dashboard's stage (for the dev hooks). */
  items(dashboard: DashboardId): StageItem[] {
    return this.scenes.get(dashboard)?.items ?? [];
  }

  /** World position of an item's centre (for the dev hooks). */
  itemWorld(dashboard: DashboardId, item: StageItem): THREE.Vector3 | undefined {
    const f = this.frames.get(dashboard);
    if (!f) return undefined;
    const cy = item.kind === 'sphere' ? item.y : item.y + item.h / 2;
    return new THREE.Vector3(item.x, cy, item.z).applyMatrix4(this.frameMatrix(f));
  }

  update(): void {
    if (this.instancesDirty) this.rebuildInstances();
    if (this.atlasDirty) this.redrawAtlas();
  }

  private rebuildInstances(): void {
    this.instancesDirty = false;
    const counts: Record<MeshKey | 'glass', number> = {
      box: 0,
      sphere: 0,
      cylinder: 0,
      figureM: 0,
      figureF: 0,
      glass: 0,
    };
    this.entries = { box: [], sphere: [], cylinder: [], figureM: [], figureF: [] };
    const dummy = new THREE.Object3D();
    const colour = new THREE.Color();
    const lineArray = this.lines.geometry.getAttribute('position') as THREE.BufferAttribute;
    let lineCount = 0;
    const tmp = new THREE.Vector3();
    const place = (key: MeshKey, id: DashboardId, item: StageItem, fm: THREE.Matrix4) => {
      if (counts[key] >= CAP[key]) return;
      dummy.updateMatrix();
      const mesh = this.meshes[key];
      mesh.setMatrixAt(counts[key], fm.clone().multiply(dummy.matrix));
      mesh.setColorAt(counts[key], colour.set(item.colour));
      this.entries[key][counts[key]++] = { dashboard: id, item };
    };
    for (const [id, scene] of this.scenes) {
      const f = this.frames.get(id);
      if (!f) continue;
      const fm = this.frameMatrix(f);
      for (const item of scene.items) {
        dummy.rotation.set(0, 0, 0);
        if (item.kind === 'box') {
          dummy.position.set(item.x, item.y + item.h / 2, item.z);
          dummy.rotation.set(0, item.rotY ?? 0, 0);
          dummy.scale.set(item.w, item.h, item.d);
          place('box', id, item, fm);
        } else if (item.kind === 'sphere') {
          dummy.position.set(item.x, item.y, item.z);
          dummy.scale.setScalar(item.r);
          place('sphere', id, item, fm);
        } else if (item.kind === 'cylinder') {
          dummy.position.set(item.x, item.y + item.h / 2, item.z);
          dummy.scale.set(item.r, item.h, item.r);
          place('cylinder', id, item, fm);
        } else {
          dummy.position.set(item.x, item.y, item.z);
          dummy.scale.setScalar(item.h);
          place(item.sex === 'F' ? 'figureF' : 'figureM', id, item, fm);
        }
      }
      for (const g of scene.glass ?? []) {
        if (counts.glass >= CAP.glass) break;
        dummy.position.set(g.x, g.y + g.h / 2, g.z);
        dummy.rotation.set(0, 0, 0);
        dummy.scale.set(g.w, g.h, g.d);
        dummy.updateMatrix();
        this.glass.setMatrixAt(counts.glass, fm.clone().multiply(dummy.matrix));
        this.glass.setColorAt(counts.glass, colour.set(g.colour));
        counts.glass++;
      }
      for (const l of scene.lines ?? []) {
        if (lineCount >= CAP.lines) break;
        tmp.set(l[0], l[1], l[2]).applyMatrix4(fm);
        lineArray.setXYZ(lineCount * 2, tmp.x, tmp.y, tmp.z);
        tmp.set(l[3], l[4], l[5]).applyMatrix4(fm);
        lineArray.setXYZ(lineCount * 2 + 1, tmp.x, tmp.y, tmp.z);
        lineCount++;
      }
    }
    lineCount = this.rebuildNotes(lineArray, lineCount, tmp);
    for (const key of Object.keys(this.meshes) as MeshKey[])
      this.finish(this.meshes[key], counts[key]);
    this.finish(this.glass, counts.glass);
    lineArray.needsUpdate = true;
    this.lines.geometry.setDrawRange(0, lineCount * 2);
  }

  /**
   * Lays out every stage's notes: a quad per note facing the table, mapped to its tile in the
   * notes atlas, and a leader line from each callout down to its object. Returns the new
   * line count.
   */
  private rebuildNotes(
    lineArray: THREE.BufferAttribute,
    lineCount: number,
    tmp: THREE.Vector3,
  ): number {
    const ctx = this.notesAtlas.getContext('2d')!;
    ctx.clearRect(0, 0, this.notesAtlas.width, this.notesAtlas.height);
    const parts: THREE.BufferGeometry[] = [];
    let tile = 0;
    const capacity = NOTE_COLS * this.noteRows;
    for (const [id, scene] of this.scenes) {
      const f = this.frames.get(id);
      if (!f || !scene.notes?.length) continue;
      const fm = this.frameMatrix(f);
      let callouts = 0;
      for (const note of scene.notes.slice(0, NOTES_PER_STAGE)) {
        if (tile >= capacity) break;
        const col = tile % NOTE_COLS;
        const row = Math.floor(tile / NOTE_COLS);
        tile++;
        ctx.save();
        ctx.translate(col * NOTE_TILE.w, row * NOTE_TILE.h);
        this.drawNote(ctx, note);
        ctx.restore();
        const size = note.kind === 'callout' ? NOTE_SIZE.callout : NOTE_SIZE.axis;
        const g = new THREE.PlaneGeometry(size.w, size.h);
        const uv = g.getAttribute('uv') as THREE.BufferAttribute;
        for (let k = 0; k < uv.count; k++)
          uv.setXY(k, (col + uv.getX(k)) / NOTE_COLS, 1 - (row + 1 - uv.getY(k)) / this.noteRows);
        let { x, y } = note;
        if (note.kind === 'callout') {
          // Stagger neighbours up a step so their boards do not cover one another; keep
          // each board inside the stage's width.
          const lift = NOTE_SIZE.callout.lift + (callouts % 2) * (size.h + 0.02);
          callouts++;
          const top = y;
          y = top + lift + size.h / 2;
          x = Math.min(STAGE.xMax - size.w / 2, Math.max(STAGE.xMin + size.w / 2, x));
          if (lineCount < CAP.lines) {
            tmp.set(note.x, top, note.z).applyMatrix4(fm);
            lineArray.setXYZ(lineCount * 2, tmp.x, tmp.y, tmp.z);
            tmp.set(x, y - size.h / 2, note.z).applyMatrix4(fm);
            lineArray.setXYZ(lineCount * 2 + 1, tmp.x, tmp.y, tmp.z);
            lineCount++;
          }
        }
        g.applyMatrix4(
          fm.clone().multiply(new THREE.Matrix4().makeTranslation(x, y, note.z + 0.01)),
        );
        parts.push(g);
      }
    }
    this.notesMesh.geometry.dispose();
    this.notesMesh.geometry = parts.length
      ? mergeGeometries(parts, false)
      : new THREE.BufferGeometry();
    for (const g of parts) g.dispose();
    this.notesTexture.needsUpdate = true;
    return lineCount;
  }

  private drawNote(ctx: CanvasRenderingContext2D, note: StageNote): void {
    const W = NOTE_TILE.w;
    const H = NOTE_TILE.h;
    if (note.kind === 'axis') {
      ctx.font = font(20, 700);
      text(ctx, truncate(ctx, note.title, W - 16), W / 2, H / 2 + 9, {
        size: 20,
        weight: 700,
        align: 'center',
        colour: note.colour,
      });
      return;
    }
    fillRoundRect(ctx, { x: 2, y: 2, w: W - 4, h: H - 4 }, 14, PALETTE.overlay);
    ctx.strokeStyle = note.colour;
    ctx.lineWidth = 3;
    ctx.beginPath();
    ctx.roundRect(3, 3, W - 6, H - 6, 13);
    ctx.stroke();
    fillRoundRect(ctx, { x: 10, y: 14, w: 8, h: H - 28 }, 4, note.colour);
    ctx.font = font(17, 700);
    text(ctx, truncate(ctx, note.title, W - 44), 30, 40, { size: 17, weight: 700 });
    if (note.detail) {
      ctx.font = font(14, 500);
      text(ctx, truncate(ctx, note.detail, W - 44), 30, 72, {
        size: 14,
        colour: PALETTE.textSoft,
      });
    }
  }

  /** Dev hook: the notes of a stage. */
  notes(dashboard: DashboardId): StageNote[] {
    return this.scenes.get(dashboard)?.notes?.slice(0, NOTES_PER_STAGE) ?? [];
  }

  /** Commits the instance buffers and refreshes the bounding sphere rays are tested against. */
  private finish(mesh: THREE.InstancedMesh, count: number): void {
    mesh.count = count;
    mesh.instanceMatrix.needsUpdate = true;
    if (mesh.instanceColor) mesh.instanceColor.needsUpdate = true;
    // InstancedMesh.raycast rejects rays outside this sphere, and it is only computed once by
    // default, so it must follow every rebuild or nothing placed later can be pointed at.
    mesh.computeBoundingSphere();
  }

  private redrawAtlas(): void {
    this.atlasDirty = false;
    const ctx = this.atlas.getContext('2d')!;
    ctx.clearRect(0, 0, this.atlas.width, this.atlas.height);
    let i = 0;
    for (const f of this.frames.values()) {
      const scene = this.scenes.get(f.dashboard);
      const col = i % this.atlasCols;
      const row = Math.floor(i / this.atlasCols);
      i++;
      if (!scene) continue;
      ctx.save();
      ctx.setTransform(
        ATLAS_SCALE,
        0,
        0,
        ATLAS_SCALE,
        col * TILE_W * ATLAS_SCALE,
        row * TILE_H * ATLAS_SCALE,
      );
      this.drawLabel(ctx, scene.label);
      ctx.restore();
    }
    this.atlasTexture.needsUpdate = true;
  }

  private drawLabel(ctx: CanvasRenderingContext2D, label: StageLabel): void {
    fillRoundRect(ctx, { x: 2, y: 2, w: TILE_W - 4, h: TILE_H - 4 }, 14, PALETTE.surface);
    ctx.font = font(24, 700);
    text(ctx, truncate(ctx, label.title, TILE_W - 40), 20, 36, { size: 24, weight: 700 });
    // Legend: the largest size at which every entry fits on one line; if none does, the
    // entries that fit at the smallest size and a "+n more" note.
    const usable = TILE_W - 40;
    let size = 17;
    const width = (s: number) => {
      ctx.font = font(s, 500);
      return label.legend.reduce((w, l) => w + 22 + ctx.measureText(l.label).width + 22, 0);
    };
    while (size > 12 && width(size) > usable) size--;
    ctx.font = font(size, 500);
    let x = 20;
    label.legend.forEach((l, i) => {
      const w = 22 + ctx.measureText(l.label).width;
      const rest = label.legend.length - i;
      if (x < 0) return;
      if (x + w > 20 + usable - (rest > 1 ? 90 : 0) && i > 0) {
        text(ctx, `+${rest} more`, x, 64, { size, colour: PALETTE.muted });
        x = -1;
        return;
      }
      fillRoundRect(ctx, { x, y: 50, w: 16, h: 16 }, 4, l.colour);
      text(ctx, l.label, x + 22, 64, { size, colour: PALETTE.text });
      ctx.font = font(size, 500);
      x += w + 22;
    });
    if (label.caption)
      text(ctx, label.caption, 20, 92, { size: 16, colour: PALETTE.muted, maxWidth: usable });
    if (label.xLabels?.length) {
      const toPx = (sx: number) => ((sx - STAGE.xMin) / (STAGE.xMax - STAGE.xMin)) * usable + 20;
      ctx.font = font(15, 600);
      const placed = placeAxisLabels(
        label.xLabels.map((l) => ({
          text: l.text,
          x: toPx(l.x),
          w: ctx.measureText(l.text).width,
        })),
        12,
        TILE_W - 12,
        14,
      );
      for (const p of placed) {
        text(ctx, p.text, p.x, TILE_H - 12, {
          size: 15,
          colour: PALETTE.text,
          align: 'left',
          maxWidth: p.maxWidth,
        });
      }
    }
  }

  dispose(): void {
    for (const m of [...Object.values(this.meshes), this.glass]) {
      m.geometry.dispose();
      (m.material as THREE.Material).dispose();
    }
    this.lines.geometry.dispose();
    (this.lines.material as THREE.Material).dispose();
    this.labelMesh.geometry.dispose();
    (this.labelMesh.material as THREE.Material).dispose();
    this.notesMesh.geometry.dispose();
    (this.notesMesh.material as THREE.Material).dispose();
    this.notesTexture.dispose();
    this.atlasTexture.dispose();
  }
}
